import React, { useEffect, useState } from "react";
import { Copy, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";
import { LoadError } from "@/admin/LoadError";
import { DELIVERY_LABEL } from "@/lib/emailStatus";

const STATUS_LABEL: Record<string, string> = {
  not_created: "Not set up", not_started: "Waiting for DNS records", pending: "Checking DNS…", verified: "Verified", failed: "Verification failed",
  temporary_failure: "Temporary DNS failure",
};

const copy = (t: string) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"), () => toast.error("Press and hold to copy"));

function Records({ records }: { records: any[] }) {
  return (
    <ul className="space-y-2" data-testid="admin-email-dns-records">
      {records.map((r, i) => (
        <li key={i} className="border rounded-lg p-3 text-xs space-y-1 bg-secondary/40">
          <p className="font-semibold">{r.record} · {r.type}{r.priority != null ? ` · priority ${r.priority}` : ""} <span className="font-normal text-muted-foreground">({r.status})</span></p>
          {[["Name / Host", r.name], ["Value", r.value]].map(([l, v]) => (
            <div key={l} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-muted-foreground">{l}</span>
              <span className="font-mono break-all flex-1 select-all">{v}</span>
              <button className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded border bg-card" onClick={() => copy(v)} aria-label={`Copy ${l}`} data-testid={`admin-email-copy-${i}-${l === "Value" ? "value" : "name"}`}><Copy className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </li>
      ))}
    </ul>
  );
}

function Stats({ s }: { s: any }) {
  return (
    <div className="space-y-2 text-xs" data-testid="admin-email-stats">
      {s.fallback_24h > 0 && (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive" data-testid="admin-email-fallback-alert">
          {s.fallback_24h} customer email{s.fallback_24h === 1 ? "" : "s"} went out through the backup service in the last 24 hours (no delivery tracking). Latest reason: {s.last_fallback?.last_reason || "unknown"}
        </p>
      )}
      <p data-testid="admin-email-webhook-health">Last delivery event received: <b>{s.last_webhook ? `${fmtDate(s.last_webhook.last_at)} (${s.last_webhook.last_type})` : "never"}</b> · branded emails accepted over 1 hour ago with no delivery confirmation: <b className={s.awaiting_confirmation_over_1h ? "text-destructive" : ""}>{s.awaiting_confirmation_over_1h}</b></p>
      <p><b>{s.total}</b> emails in the last 30 days · branded {s.by_route.resend || 0} · old service {s.by_route.managed || 0} (of which customer backups {s.fallback_30d}) · suppressed addresses {s.suppressed_addresses}</p>
      <div className="flex flex-wrap gap-1">{Object.entries(s.by_delivery).map(([k, n]) => <span key={k} className="rounded-full px-2 py-0.5 bg-secondary">{DELIVERY_LABEL[k] || k}: {String(n)}</span>)}</div>
      {s.recent_failures.length > 0 && (
        <ul className="space-y-1" data-testid="admin-email-failures">
          {s.recent_failures.map((f: any) => <li key={f.id} className="text-muted-foreground">{fmtDate(f.created_at)} · {f.template} · {f.delivery_status || f.status} · company {String(f.org_id || "").slice(0, 8)}{f.fallback_reason ? " · via backup" : ""}{f.last_error ? ` · ${f.last_error}` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}

export function AdminEmail() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => { setErr(""); api.get("/admin/email").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); };
  useEffect(() => { load(); }, []);
  const act = async (verify: boolean) => {
    setBusy(true);
    try { await api.post("/admin/email/domain", null, { params: { verify } }); load(); toast.success(verify ? "Verification requested" : "Domain details refreshed"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (err) return <LoadError what="email settings" detail={err} onRetry={load} testid="admin-email-error" />;
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const dom = d.domain;
  return (
    <section className="bg-card border rounded-xl p-4 space-y-3" data-testid="admin-email">
      <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Branded sending domain</h3>
      <p className="text-sm"><span className="font-mono">{dom.name}</span> · <b data-testid="admin-email-domain-status">{STATUS_LABEL[dom.status] || dom.status}</b>{dom.checked_at ? <span className="text-xs text-muted-foreground"> · checked {fmtDate(dom.checked_at)}</span> : null}</p>
      <p className="text-xs text-muted-foreground" data-testid="admin-email-route-note">
        {d.branded_active ? "Customer estimates and confirmations send as “Company via Instant Quote Wizard” from this domain."
          : "Branded sending is inactive: customer emails go through the backup service as “Instant Quote Wizard” (no delivery tracking) until this domain is confirmed verified."}
      </p>
      {dom.last_error && (
        <p role="alert" className="text-xs rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive" data-testid="admin-email-domain-error">
          Last check with Resend failed{dom.last_error_at ? ` (${fmtDate(dom.last_error_at)})` : ""}: {dom.last_error}. The app keeps the last known status and re-checks every 10 minutes.
        </p>
      )}
      {dom.records?.length > 0 && dom.status !== "verified" && <><p className="text-xs">Add these records at your DNS provider for instantquotewizard.com, then press Check verification. DMARC tip: add a TXT record at <span className="font-mono">_dmarc.instantquotewizard.com</span> with <span className="font-mono">v=DMARC1; p=none; rua=mailto:you@yourdomain</span> and tighten to quarantine later.</p><Records records={dom.records} /></>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="h-11" disabled={busy} onClick={() => act(false)} data-testid="admin-email-setup">{dom.resend_id ? <><RefreshCw className="h-4 w-4 mr-2" />Refresh records</> : "Set up domain"}</Button>
        {dom.resend_id && dom.status !== "verified" && <Button className="h-11" disabled={busy} onClick={() => act(true)} data-testid="admin-email-verify">Check verification</Button>}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="admin-email-webhook-url">Resend webhook endpoint for this environment: <span className="font-mono break-all">{window.location.origin}{d.webhook_path}</span></p>
      <Stats s={d.stats} />
    </section>
  );
}
